import type { FC } from "react";
import React, { useEffect, useRef } from "react";

import { keyframes } from "@emotion/css";
import styled from "@emotion/styled";
import {
  IonButton,
  IonButtons,
  IonContent,
  IonHeader,
  IonModal,
  IonTitle,
  IonToolbar
} from "@ionic/react";
import clsx from "clsx";
import { useSearchParams } from "react-router-dom";
import { IconCrossXCloseMissed } from "src/constants/icons";
import { APP_CONTENT_WIDTH_WITHOUT_PADDING } from "src/constants/layout";
import { isPlatform } from "src/lib/platform";
import useGoToOrBack from "src/ui/hooks/useGoToOrBack";

import { AppQueryPopupsController } from "../AppQueryPopups/AppQueryPopupsBloc";

const CustomStylesModal = styled(IonModal)`
  label: CustomStyles;
  --dialog-background: var(--color-cream);

  @media only screen and (min-width: 768px) and (min-height: 768px) {
    --height: max(60vh, 600px);

    &[data-fullscreen="true"] {
      --height: 100vh;
      --width: 100vw;
    }
  }

  &[data-dialog-type="feedback"] {
    nine-funnel-step {
      width: 100%;
      --funnel-step-width: min(100%, 55rem);
    }
  }

  ion-toolbar {
    --background: var(--toolbar-background, var(--dialog-background));
  }

  ion-content {
    --background: var(--dialog-background);
  }

  ion-title {
    font-size: 1rem;
    font-weight: 500;
  }

  .close {
    svg {
      width: 2rem;
      height: auto;
      color: var(--color-charcoal);
    }
  }

  ion-content > *:last-child {
    padding-bottom: calc(var(--keyboard-height, 50vh) + 2rem);
  }

  ion-content.no-padding > *:last-child {
    padding-bottom: 0;
  }

  nine-funnel-step {
    --margin-top: 1rem;
    width: min(100%, 100vw, ${APP_CONTENT_WIDTH_WITHOUT_PADDING}px);
    --funnel-step-padding: 0;
  }

  &[data-custom-z-index="true"] {
    z-index: var(--z-index) !important;
  }
`;

const fadeInKeyframes = keyframes`
    0% {
        opacity: 0;
    }
    100% {
        opacity: 1;
    }
`;

const BgGradient = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: -1;

  &::before {
    content: "";
    opacity: 0.75;
    position: absolute;
    inset: 0;
    background:
      linear-gradient(
        180deg,
        var(--color-cream) 0%,
        var(--color-cream-transparent) 90%
      ),
      linear-gradient(
        85deg,
        var(--color-sunrise-blue) 1.49%,
        var(--color-sunrise-pink) 46.22%,
        var(--color-sunrise-orange) 81.11%
      );
  }

  animation: ${fadeInKeyframes} 1.2s linear forwards;
`;

const InDialog: FC<{
  isOpen?: boolean;
  hideHeader?: boolean;
  returnUrl?: string;
  isFullScreen?: boolean;
  alwaysUseReturnUrl?: boolean;
  onClose?: () => void;
  onWillDismiss?: () => void;
  children?: React.ReactNode;
  title?: string;
  setRef?: (ref: React.RefObject<HTMLIonModalElement>) => void;
  popup?: boolean;
  className?: string;
  simple?: boolean;
  backdropDismiss?: boolean;
  height?: string;
  width?: string;
  alwaysModal?: boolean;
  showClose?: boolean;
  dialogType?: string;
  background?: string;
  noPadding?: boolean;
}> = (props) => {
  const modal = useRef<HTMLIonModalElement>(null);
  const contentElRef = useRef<HTMLIonContentElement>(null);
  const { isOpen = true, showClose = true } = props;
  const [query] = useSearchParams();

  const presentationEl = isOpen
    ? document.getElementById("ion-page-router")
    : null;
  const windowWidth = isOpen ? window.innerWidth : 0;

  let zIndex: string | null | undefined = query.get("z");
  if (zIndex === "0") {
    zIndex = undefined;
  }

  useEffect(() => {
    // prevent touchmove and mousemove events from closing the dialog
    if (contentElRef.current) {
      contentElRef.current.addEventListener("touchmove", (e) => {
        e.stopPropagation();
      });
      contentElRef.current.addEventListener("mousemove", (e) => {
        e.stopPropagation();
      });
    }
  }, [contentElRef.current]);

  const navigate = useGoToOrBack();
  const handleClose = () => {
    if (props.returnUrl) {
      navigate(props.returnUrl, {
        multiBack: true
      });
    }

    props.onWillDismiss?.();
    props.onClose?.();
    AppQueryPopupsController.closePopup();
  };

  useEffect(() => {
    AppQueryPopupsController.setModalDismiss(modal.current);
  }, [modal.current]);

  const style: Record<string, unknown> = {
    "--z-index": zIndex,
    "--dialog-background": props.background
  };

  if (windowWidth >= 600 || props.alwaysModal) {
    if (props.height) {
      style["--height"] = props.height;
    }
    if (props.width) {
      style["--width"] = props.width;
    }
  }

  const usePresentingElement = props.alwaysModal
    ? undefined
    : windowWidth < 600
      ? presentationEl
      : undefined;

  if (!props.children) {
    return null;
  }

  return (
    <CustomStylesModal
      ref={modal}
      isOpen={isOpen}
      presentingElement={usePresentingElement ?? undefined}
      mode={isPlatform("android") ? "md" : "ios"}
      className={clsx(
        props.className,
        props.simple && "modal-simple",
        props.alwaysModal && "modal-always-modal"
      )}
      style={style}
      data-custom-z-index={Boolean(zIndex)}
      backdropDismiss={props.backdropDismiss}
      onWillDismiss={handleClose}
      data-fullscreen={props.isFullScreen}
      data-dialog-type={props.dialogType}
    >
      {!props.hideHeader && (
        <IonHeader>
          <IonToolbar>
            {showClose && (
              <IonButtons slot="end">
                <IonButton
                  aria-label="Close"
                  onClick={() => void modal.current?.dismiss()}
                  className="close"
                >
                  <IconCrossXCloseMissed aria-hidden="true" />
                </IonButton>
              </IonButtons>
            )}
            {props.title && <IonTitle>{props.title}</IonTitle>}
            <IonButtons slot="end"></IonButtons>
          </IonToolbar>
        </IonHeader>
      )}
      <IonContent
        scrollEvents
        className={clsx("ion-padding", props.noPadding && "no-padding")}
        ref={contentElRef}
      >
        {props.children}
        {props.background === "gradient" && <BgGradient />}
      </IonContent>
    </CustomStylesModal>
  );
};

export default InDialog;
